import React, { FC, useState, useEffect, ClipboardEvent } from 'react';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import UpRightArrow from '@mui/icons-material/NorthEast';
import Schedule from '@mui/icons-material/Schedule';
import Send from '@mui/icons-material/Send';
import TaskIcon from '@mui/icons-material/Task';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { PrimaryBtn } from 'components/UI/Buttons';
import { usePostFileMessageMutation, usePostMessageMutation } from 'services/api/messages';
import { usePostScheduledMessageMutation } from 'services/api/scheduledMessages.api';
import { useGetTasksByClientQuery } from 'services/api/tasks.api';
import { Client } from 'services/types/client';
import { Message, MessageStatus } from 'services/types/message';
import { ScheduledMessage } from 'services/types/scheduledMessage';

import TaskModals from './TaskModals';

interface Props {
  client: Client;
  conversation: Message[] | undefined;
  setConversation: (messages: Message[]) => void;
  addScheduledMessage: (message: ScheduledMessage) => void;
  onRefetchClient?: () => void;
}

type Hour = {
  value: number;
  label: string;
};

const ChatFooter: FC<Props> = ({ client, conversation, setConversation, addScheduledMessage, onRefetchClient }) => {
  const [active, setActive] = useState(false);
  const [messageToSend, setMessage] = useState('');
  const [fileToSend, setFileToSend] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [selectedHour, setSelectedHour] = useState<number | ''>('');
  const [selectedMinute, setSelectedMinute] = useState<number | ''>('');
  const [availableHours, setAvailableHours] = useState<Hour[]>([]);

  // Task-related state
  const [openTaskModal, setOpenTaskModal] = useState(false);
  const [openTaskTransitionModal, setOpenTaskTransitionModal] = useState(false);

  const [postMessageMutation, { data: newMessage, isLoading: isSendingMessage }] = usePostMessageMutation();
  const [postFileMutation, { data: newFileMessage, isLoading: isSendingFile }] = usePostFileMessageMutation();
  const [postScheduledMessageMutation, { isLoading: isSchedulingMessage }] = usePostScheduledMessageMutation();

  // Task queries
  const { data: tasks = [], refetch: refetchTasks } = useGetTasksByClientQuery(client.id);

  const updateAvailableHours = () => {
    const lastUserMessage = conversation
      ?.filter((msg) => msg.role === 'user')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    if (!lastUserMessage) {
      setScheduleError('No hay mensajes previos del usuario, debes usar un template para comenzar una conversación');
      return;
    }

    const now = new Date();
    const currentHour = now.getHours();
    const lastMessageTime = new Date(lastUserMessage.createdAt);
    const maxTime = new Date(lastMessageTime.getTime() + 23 * 60 * 60 * 1000);
    const maxHour = maxTime.getHours();

    const hoursMaxLimit = (maxTime.getTime() - now.getTime()) / (1000 * 60 * 60);
    const hours: Hour[] = [];
    let hour = currentHour;

    while (hour <= 23 && hours.length < hoursMaxLimit) {
      hours.push({ value: hour, label: hours.length === 0 ? `Hoy: ${hour} hrs` : `${hour} hrs` });
      hour += 1;
    }
    hour = 0;
    while (hour <= maxHour && hours.length < hoursMaxLimit) {
      hours.push({ value: hour, label: hour === 0 ? `Mañana: ${hour} hrs` : `${hour} hrs` });
      hour += 1;
    }

    setAvailableHours(hours);
    setSelectedHour(hours[1]?.value ?? '');
    setSelectedMinute(0);
  };

  useEffect(() => {
    if (openScheduleModal) {
      updateAvailableHours();
    }
  }, [openScheduleModal]);

  const handleScheduleMessage = () => {
    if (messageToSend.length === 0 || selectedHour === '' || selectedMinute === '') {
      return;
    }

    const scheduledTime = new Date();
    scheduledTime.setHours(selectedHour, selectedMinute, 0, 0);

    postScheduledMessageMutation({
      clientId: client.id,
      text: messageToSend,
      postAt: scheduledTime.toISOString(),
    })
      .then((response) => {
        if ('data' in response) {
          addScheduledMessage(response.data);
          setMessage('');
          setOpenScheduleModal(false);
        }
        if ('error' in response) {
          setScheduleError(
            `Error al programar el mensaje. ${'data' in response.error ? String(response.error.data) : ''}`
          );
          console.error('Error al programar el mensaje', response.error); // eslint-disable-line no-console
        }
      })
      .catch((err) => {
        setScheduleError('Error al programar el mensaje');
        console.error('Error al programar el mensaje', err); // eslint-disable-line no-console
      });
  };

  const handleSendMessage = () => {
    if (messageToSend.length === 0) {
      return;
    }
    // eslint-disable-next-line no-console
    postMessageMutation({ clientId: client.id, text: messageToSend }).catch((err) => console.error(err));
    setMessage('');
  };

  // Task management functions
  const currentTask = tasks.find((task) => task.status === 'IN_PROGRESS');
  const hasActiveTask = Boolean(currentTask);

  const handleTaskCreated = () => {
    refetchTasks().catch((err) => console.error('Error refetching tasks', err)); // eslint-disable-line no-console
    onRefetchClient?.();
  };

  const handleTaskTransitioned = () => {
    refetchTasks().catch((err) => console.error('Error refetching tasks', err)); // eslint-disable-line no-console
    onRefetchClient?.();
  };

  useEffect(() => {
    if (conversation) {
      const yesterday = new Date();
      yesterday.setHours(new Date().getHours() - 24);
      if (conversation.find((message) => message.role === 'user' && new Date(message.createdAt) > yesterday)) {
        setActive(true);
      } else {
        setActive(false);
      }
    }
  }, [conversation]);

  useEffect(() => {
    if (newMessage && conversation) {
      const newMessageWithSentStatus = { ...newMessage, status: 'sent' as MessageStatus };
      setConversation([...conversation, newMessageWithSentStatus]);
    }
  }, [newMessage]);

  useEffect(() => {
    if (newFileMessage && conversation) {
      setConversation([...conversation, { ...newFileMessage, status: 'sent' as MessageStatus }]);
    }
  }, [newFileMessage]);

  useEffect(() => {
    if (fileToSend) {
      setOpenModal(true);
      if (fileToSend.size > 5000000) {
        setError('El archivo no puede pesar más de 5mb');
        return () => {};
      }
      const objectUrl = URL.createObjectURL(fileToSend);
      setFilePreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    return () => {};
  }, [fileToSend]);

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData?.items;

    if (!items || items.length === 0) return;

    const file = items[0].getAsFile();
    if (file) {
      setFileToSend(file);
      e.preventDefault();
    }
  };

  if (!active) {
    return (
      <Grid container style={{ padding: '20px' }} alignItems="center" flexDirection="column">
        {conversation?.length === 0 ? (
          <Typography>No se ha hablado con este usuario, envíe una plantilla para comenzar una conversación</Typography>
        ) : (
          <Typography>Chat no está activo, pasaron más de 24 horas desde el último contacto del usuario</Typography>
        )}
        <Typography>Puedes enviar un template usando las opciones del chat</Typography>
        <UpRightArrow fontSize="small" sx={{ ml: 1 }} />
      </Grid>
    );
  }

  return (
    <>
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90vw', sm: '80vw', md: '70vw', lg: '60vw' },
            maxWidth: '900px',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
            overflow: 'auto',
          }}
        >
          {error && (
            <>
              <Typography variant="h5" color="error">
                {error}
              </Typography>
              <PrimaryBtn
                variant="contained"
                color="error"
                onClick={() => {
                  setFileToSend(null);
                  setOpenModal(false);
                  setError(null);
                }}
                sx={{ mr: 2 }}
              >
                Cerrar
              </PrimaryBtn>
            </>
          )}
          {!error && (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <Typography variant="h5" mb={2}>
                {fileToSend?.name.includes('.pdf') ? 'Archivo' : 'Imagen'} a enviar: {fileToSend?.name}
              </Typography>
              {filePreview && (
                <Box
                  sx={{
                    width: '100%',
                    maxHeight: 'calc(90vh - 200px)',
                    overflow: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                  }}
                >
                  {fileToSend?.name.includes('.pdf') ? (
                    <embed src={filePreview} width="100%" height="calc(90vh - 200px)" style={{ minHeight: '400px' }} />
                  ) : (
                    <img
                      src={filePreview}
                      alt={fileToSend?.name ?? 'file to send'}
                      style={{
                        maxWidth: '100%',
                        maxHeight: 'calc(90vh - 200px)',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                  )}
                </Box>
              )}
              <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
                <PrimaryBtn
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setFileToSend(null);
                    setOpenModal(false);
                  }}
                >
                  Cancelar
                </PrimaryBtn>
                <PrimaryBtn
                  variant="contained"
                  onClick={() => {
                    if (fileToSend) {
                      // eslint-disable-next-line no-console
                      postFileMutation({ clientId: client.id, file: fileToSend }).catch((err) => console.error(err));
                      setFileToSend(null);
                      setOpenModal(false);
                    }
                  }}
                  disabled={isSendingFile}
                >
                  {isSendingFile ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    <>
                      Enviar
                      <Send fontSize="small" sx={{ ml: 1 }} />
                    </>
                  )}
                </PrimaryBtn>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
      {openScheduleModal && (
        <Modal open onClose={() => setOpenScheduleModal(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography variant="h6" mb={2}>
              Programar mensaje
            </Typography>
            <Box display="flex" gap={2} mb={2}>
              <FormControl fullWidth>
                <InputLabel id="hour-select-label">Hora</InputLabel>
                <Select
                  labelId="hour-select-label"
                  value={selectedHour}
                  label="Hora"
                  onChange={(e) => setSelectedHour(e.target.value as number)}
                >
                  {availableHours.map((hour) => (
                    <MenuItem key={hour.value} value={hour.value} divider={hour.value === 23}>
                      {hour.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="minute-select-label">Minutos</InputLabel>
                <Select
                  labelId="minute-select-label"
                  value={selectedMinute}
                  label="Minutos"
                  onChange={(e) => setSelectedMinute(e.target.value as number)}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <MenuItem key={i * 5} value={i * 5}>
                      {i * 5}m
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {scheduleError && (
              <Typography color="error" mb={2}>
                {scheduleError}
              </Typography>
            )}
            <Box mb={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
              <Typography variant="body2" color="text.secondary">
                El mensaje se enviará a las:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {selectedHour}:{selectedMinute.toString().padStart(2, '0')} hrs
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Mensaje:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {messageToSend}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={() => setOpenScheduleModal(false)}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleScheduleMessage}
                disabled={selectedHour === '' || selectedMinute === '' || isSchedulingMessage}
              >
                {isSchedulingMessage ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Programar'}
              </Button>
            </Box>
          </Box>
        </Modal>
      )}

      <TaskModals
        clientId={client.id}
        openTaskModal={openTaskModal}
        openTaskTransitionModal={openTaskTransitionModal}
        currentTask={currentTask}
        onCloseTaskModal={() => setOpenTaskModal(false)}
        onCloseTaskTransitionModal={() => setOpenTaskTransitionModal(false)}
        onTaskCreated={handleTaskCreated}
        onTaskTransitioned={handleTaskTransitioned}
      />

      {/* Active Task Indicator */}
      {hasActiveTask && currentTask && (
        <Box
          sx={{
            backgroundColor: '#fff3cd',
            borderLeft: '4px solid #ffc107',
            p: 2,
            mb: 1,
          }}
        >
          <Typography variant="body2" color="text.primary">
            <strong>Tarea activa:</strong> {currentTask.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Los mensajes están bloqueados mientras hay una tarea en progreso. Haz clic en el ícono de tarea para
            administrarla.
          </Typography>
        </Box>
      )}

      <Box
        display="flex"
        width="100%"
        py={1}
        justifyContent="space-between"
        alignItems="center"
        sx={{
          backgroundColor: '#fff',
        }}
      >
        <Box display="flex" alignItems="center">
          <label htmlFor="file-input">
            <input
              id="file-input"
              name="file-input"
              type="file"
              style={{ display: 'none' }}
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={(e) => {
                if (e.target.files) {
                  setFileToSend(e.target.files[0]);
                }
              }}
            />
            <Button variant="text" component="span" disabled={hasActiveTask}>
              <AttachFileIcon fontSize="large" />
            </Button>
          </label>
          <IconButton
            title={hasActiveTask ? 'Administrar tarea activa' : 'Crear nueva tarea'}
            size="large"
            onClick={() => {
              if (hasActiveTask) {
                setOpenTaskTransitionModal(true);
              } else {
                setOpenTaskModal(true);
              }
            }}
            color={hasActiveTask ? 'warning' : 'default'}
          >
            <TaskIcon fontSize="large" />
          </IconButton>
        </Box>
        <TextField
          id="outlined-basic-email"
          label={hasActiveTask ? 'Hay una tarea en progreso' : 'Mensaje'}
          fullWidth
          multiline
          onPaste={handlePaste}
          sx={{
            backgroundColor: '#fff',
            borderRadius: '40px',
            '& .MuiOutlinedInput-root': { borderRadius: '40px' },
          }}
          placeholder={
            hasActiveTask
              ? 'No puedes enviar mensajes mientras hay una tarea activa. Completa o cancela la tarea primero.'
              : 'Escribe un mensaje... (también puedes pegar un archivo o screenshot)'
          }
          value={messageToSend}
          onChange={(e) => setMessage(e.target.value)}
          disabled={isSendingMessage || hasActiveTask}
        />
        <Box display="flex" flexDirection="row" alignItems="center">
          <IconButton
            title="Enviar mensaje"
            size="large"
            onClick={handleSendMessage}
            disabled={messageToSend.length === 0 || isSendingMessage || hasActiveTask}
          >
            {isSendingMessage ? <CircularProgress size={24} /> : <Send fontSize="large" />}
          </IconButton>
          <IconButton
            title="Programar mensaje"
            size="small"
            onClick={() => setOpenScheduleModal(true)}
            disabled={messageToSend.length === 0 || isSendingMessage || hasActiveTask}
          >
            <Schedule fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </>
  );
};

ChatFooter.defaultProps = {
  onRefetchClient: () => {},
};

export default ChatFooter;
